import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useVisibleContacts, useTeam } from '../../pages/crm/useCrm';
import { usePipelineRows as usePipeline } from '../../pages/crm/ContactExtras';
import { watchPath } from '../../services/crmService';
import { buildPersonSummary, telHref, mailHref } from '../../services/personSummary';

// Slide-over "who is this?" panel. Read-only: it only listens to data the user can already see.
// person = { contactId?, sellerInterestId?, buyerInterestId?, transaction?, name? }
function PanelBody({ person, onClose }) {
  const contacts = useVisibleContacts({ includeArchived: true, leadsOnly: false }); // deal-linked people still resolve
  const { sellers, buyers } = usePipeline();
  const team = useTeam();
  const rec = person.sellerInterestId ? sellers.find((r) => r.id === person.sellerInterestId) : person.buyerInterestId ? buyers.find((r) => r.id === person.buyerInterestId) : null;
  const cid = person.contactId || (rec && rec.contactId) || (person.transaction && person.transaction.crmContactId);
  const contact = cid ? contacts.find((c) => c.id === cid) : null;
  const [activity, setActivity] = useState([]);
  useEffect(() => (contact ? watchPath(`crm/activity/${contact.id}`, setActivity) : undefined), [contact?.id]); // eslint-disable-line react-hooks/exhaustive-deps
  const s = buildPersonSummary({ contact, rec, recKind: person.buyerInterestId ? 'Buyer' : 'Seller', transaction: person.transaction, team, activity, fallbackName: person.name });
  const row = (label, value) => (value ? (
    <div style={{ marginBottom: 10 }}>
      <div style={lbl}>{label}</div>
      <div style={{ color: '#1a1a1a', overflowWrap: 'anywhere' }}>{value}</div>
    </div>) : null);
  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, borderBottom: '1px solid #d8c9a8', paddingBottom: 10, marginBottom: 12 }}>
        <div style={{ minWidth: 0 }}>
          <h2 id="person-peek-title" style={{ margin: 0, fontSize: '1.3rem', color: '#1a1a1a', overflowWrap: 'anywhere' }}>{s.name}</h2>
          {s.kind && <div style={{ fontSize: 13, color: '#8C6D1F', fontWeight: 600 }}>{s.kind}{s.dnc ? ' · On Do Not Call' : ''}</div>}
        </div>
        <button type="button" onClick={onClose} aria-label="Close profile" style={{ border: '1px solid #b8a57a', background: '#fff', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 16 }}>✕</button>
      </div>
      {row('Phone', s.phone && (telHref(s.phone) && !s.dnc ? <a href={telHref(s.phone)} style={lnk}>{s.phone}</a> : s.phone))}
      {row('Email', s.email && (mailHref(s.email) ? <a href={mailHref(s.email)} style={lnk}>{s.email}</a> : s.email))}
      {row('Lead source', s.source)}
      {row('Date added', s.added)}
      {row('Status / stage', s.stage)}
      {row('Assigned agent', s.agent)}
      {row('Property', s.address && (s.transactionId ? <Link to={`/transactions/${s.transactionId}`} style={lnk}>{s.address}</Link> : s.address))}
      {row('Closing date', s.closingDate)}
      {row('Last contact', s.lastContact)}
      {row('Note', s.pinnedNote && <span style={{ whiteSpace: 'pre-wrap' }}>{String(s.pinnedNote).slice(0, 400)}</span>)}
      {s.notes.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={lbl}>Recent activity</div>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {s.notes.map((n, i) => <li key={i} style={{ fontSize: 14, marginBottom: 4, overflowWrap: 'anywhere' }}>{n.at && <span style={{ color: '#7f7f7f' }}>{n.at} · </span>}{n.text.slice(0, 200)}</li>)}
          </ul>
        </div>)}
      {!contact && !rec && !person.transaction && <p style={{ color: '#7f7f7f', fontSize: 14 }}>No linked record found that you can view.</p>}
      {s.fullLink && <Link to={s.fullLink} style={{ display: 'inline-block', marginTop: 8, padding: '8px 14px', borderRadius: 6, background: '#1a1a1a', color: '#D4AF37', fontWeight: 700, textDecoration: 'none' }}>Open full profile</Link>}
    </>
  );
}

export default function PersonPeekPanel({ person, onClose }) {
  const panelRef = useRef(null);
  useEffect(() => {
    if (!person) return undefined;
    const prevFocus = document.activeElement;
    const y = window.scrollY;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    setTimeout(() => panelRef.current && panelRef.current.focus(), 0);
    return () => {
      document.removeEventListener('keydown', onKey);
      window.scrollTo(0, y);
      if (prevFocus && prevFocus.focus) prevFocus.focus({ preventScroll: true });
    };
  }, [person, onClose]);
  if (!person) return null;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000 }}>
      <div onClick={onClose} aria-hidden="true" style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }} />
      <aside ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="person-peek-title"
        style={{ position: 'absolute', top: 0, right: 0, height: '100%', width: 'min(420px, 100vw)', boxSizing: 'border-box', background: '#fff', borderLeft: '4px solid #D4AF37', padding: '1rem 1.1rem', overflowY: 'auto', overflowX: 'hidden', boxShadow: '-4px 0 16px rgba(0,0,0,0.15)', outline: 'none' }}>
        <PanelBody person={person} onClose={onClose} />
      </aside>
    </div>
  );
}

// Inline tappable name that opens the panel.
export function PersonName({ children, onOpen, style }) {
  return (
    <button type="button" onClick={(e) => { e.stopPropagation(); onOpen(); }}
      style={{ background: 'none', border: 'none', padding: 0, margin: 0, font: 'inherit', color: '#8C6D1F', textDecoration: 'underline', cursor: 'pointer', textAlign: 'left', ...style }}>
      {children}
    </button>
  );
}

const lbl = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8C6D1F' };
const lnk = { color: '#8C6D1F', fontWeight: 600 };
